/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import Button from '@components/atoms/ui-kit/button.tsx';
import toast from 'react-hot-toast';
import { useGetResellerTokens } from '@utils/api/resellers/use-get-reseller-customer-token.tsx';
import { useCreateResellerToken } from '@utils/api/resellers/use-create-reseller-token.tsx';
import { useDeleteResellerToken } from '@utils/api/resellers/use-delete-reseller-token.tsx';
import {
  ResellerToken,
  ResellerTokens
} from '@utils/types/resellers/reseller-token.ts';
import { useTranslation } from 'react-i18next';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { AxiosError, isAxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Copy, MessageCircleWarning, Plus, Trash2 } from 'lucide-react';
import RadioGroup from '@components/atoms/ui-kit/raido-group.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';

const defaultYears = [1, 2, 5];

type Row = {
  id: number;
  key: string;
  name: string;
  type: string;
  expires: string;
};

const TokenDetails = ({ resellerId }: { resellerId?: string }) => {
  const { t } = useTranslation();

  const yearsValid = useMemo(
    () =>
      defaultYears.map(id => ({
        id,
        name: t(`${id}-year`),
        description: id === 1 ? t('recommended') : ''
      })),
    [t]
  );

  const types = [
    { name: 'batch', description: t('store-the-data-then-process-it') },
    { name: 'realtime', description: t('process-data-as-it-comes') }
  ];

  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<
    ResellerTokens | undefined
  >();
  const [tokenType, setTokenType] = useState(types[0]);
  const [tokenExpires, setTokenExpires] = useState(yearsValid[0].id);
  const [addToken, setAddToken] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createTokenMutation = useCreateResellerToken(Number(resellerId));
  const { mutate: deleteToken, isLoading: deleteTokenLoading } =
    useDeleteResellerToken(Number(resellerId));
  const { data, isLoading } = useGetResellerTokens(resellerId || '');

  const rows: Row[] = useMemo(
    () =>
      Array.isArray(data?.result)
        ? data.result.map((token: ResellerToken) => ({
            id: token.id || 0,
            key: token.key || '',
            name: token.name || '',
            type: token.type || '',
            expires: token.expires || ''
          }))
        : [],
    [data]
  );

  const columns: DataGridColumn<Row>[] = useMemo(
    () => [
      { id: 'id', header: t('id'), accessorKey: 'id', maxWidth: 100 },
      { id: 'key', header: t('key'), accessorKey: 'key', minWidth: 240 },
      { id: 'name', header: t('name'), accessorKey: 'name', minWidth: 200 },
      {
        id: 'type',
        header: t('type'),
        accessorKey: 'type',
        minWidth: 140,
        cell: ({ getValue }) => String(getValue() ?? '').toLowerCase()
      },
      {
        id: 'expires',
        header: t('expires'),
        accessorKey: 'expires',
        minWidth: 200
      }
    ],
    [t]
  );

  const handleDeleteToken = (id: number) => {
    if (!id || deleteTokenLoading) return;

    const loadingToastId = toast.loading(t('deleting-token'));
    deleteToken(id, {
      onSuccess: () => {
        toast.success(t('token-deleted-successfully'), { id: loadingToastId });
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error?.response?.data;
        let messages: string[] = [];

        if (Array.isArray(data?.detail)) {
          messages = data.detail.map(err => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [t('there-was-an-error-deleting-the-token')];
        }

        toast.error(messages.join('\n'), { id: loadingToastId });
      }
    });
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => handleDeleteToken(row.id)
      }
    ],
    [t, deleteTokenLoading]
  );

  const handleSubmit = async () => {
    if (!tokenName) {
      toast.error(t('token-name-is-required'));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createTokenMutation.mutateAsync({
        name: tokenName,
        type: tokenType.name,
        years_valid: tokenExpires
      });

      setCreatedToken(result);
      setTokenName('');
      setTokenType(types[0]);
      setTokenExpires(yearsValid[0].id);
      toast.success(t('token-created-successfully'));
    } catch (error) {
      let message = t('there-was-an-error-creating-the-token');

      if (isAxiosError<BaseError>(error)) {
        const data = error.response?.data;
        message =
          data?.error_description ||
          data?.detail ||
          data?.error ||
          data?.message ||
          message;
      }

      toast.error(message);
    } finally {
      setAddToken(false);
      setIsSubmitting(false);
    }
  };

  const FORM_ID = 'create-token-form';

  const typeOptions = useMemo(
    () =>
      types.map(t => ({
        value: t.name.toLowerCase(),
        label: t.name.toUpperCase(),
        description: `(${t.description})`
      })),
    [types]
  );

  const years_valid = [
    { id: 1, name: t('1-year'), description: t('recommended') },
    { id: 2, name: t('2-years'), description: '' },
    { id: 5, name: t('5-years'), description: '' }
  ];

  const expiresOptions = useMemo(
    () =>
      years_valid.map(y => ({
        value: String(y.id),
        label: y.name,
        description: y.description
      })),
    [years_valid]
  );

  return (
    <div className="p-4 space-y-2">
      <div className="flex justify-between items-center">
        {createdToken?.result ? (
          <div className="flex items-center space-x-2 py-1">
            <p className="text-sm border border-gray-200 dark:border-yellow-900/60 rounded-2xl p-2 bg-yellow-100 dark:bg-yellow-950/40 text-black dark:text-yellow-200">
              {t('your-newly-created-token')}{' '}
              <strong>{createdToken.result.key}</strong>
            </p>
            <Copy
              width={14}
              className="h-5 w-5 text-gray-500 dark:text-zinc-400 cursor-pointer"
              onClick={() => {
                navigator.clipboard
                  .writeText(createdToken.result.key)
                  .then(() => toast.success(t('token-copied-to-clipboard')))
                  .catch(() =>
                    toast.error(t('failed-to-copy-token-to-clipboard'))
                  );
              }}
            />
          </div>
        ) : (
          <div />
        )}
        <Button
          onClick={() => setAddToken(true)}
          icon={<Plus width={18} />}
          size="small"
          label={t('add-token')}
          variant="secondary"
          className="w-fit"
        />
      </div>

      <Modal
        isOpen={addToken}
        onClose={() => setAddToken(false)}
        size="medium"
        title={t('add-token')}
        footerActions={[
          <Button
            key="cancel"
            onClick={() => setAddToken(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            label={isSubmitting ? t('creating') : t('create')}
            icon={<Plus width={18} />}
            size="small"
            onClick={handleSubmit}
            variant="primary"
          />
        ]}
      >
        <form
          id={FORM_ID}
          className="px-2"
          onSubmit={e => {
            e.preventDefault();
            if (!isSubmitting) handleSubmit();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <p className="text-primary-bg pb-4 border-b border-gray-200 dark:border-zinc-700 mb-4">
            {t('token-generation-description')}
          </p>

          <Input
            id="name"
            label={t('name')}
            placeholder={t('name')}
            onChange={e => setTokenName(e.target.value)}
            type="text"
            autoComplete="given-name"
          />

          <div className="border-b border-gray-900/10 dark:border-zinc-700 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium leading-6 pb-2">
                    {t('type')} *
                  </h2>
                </div>

                <RadioGroup
                  name="token_type"
                  options={typeOptions}
                  value={tokenType.name.toLowerCase()}
                  onChange={v =>
                    setTokenType(
                      types.find(t => t.name.toLowerCase() === v) ?? types[0]
                    )
                  }
                  size="small"
                  wrapperClass="w-full"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium leading-6 pb-2">
                    {t('expires')} *
                  </h2>
                </div>

                <RadioGroup
                  name="token_expires"
                  options={expiresOptions}
                  value={String(tokenExpires)}
                  onChange={v => setTokenExpires(Number(v))}
                  size="small"
                  wrapperClass="w-full"
                />
              </div>
            </div>
          </div>

          <p className="text-xs mt-6 border-t pt-2 text-yellow-500 flex items-center gap-2">
            <MessageCircleWarning width={20} />
            {t('api-token-validity')}
          </p>
        </form>
      </Modal>

      <div className="pt-2">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting={false}
          manualSorting={false}
          enableFiltering={false}
          manualFiltering={false}
          totalItems={data?.total_results ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="56vh"
          dense
          grid={false}
          striped
          loading={isLoading || deleteTokenLoading || !data}
          getRowId={row => String(row.id)}
        />
      </div>
    </div>
  );
};

export default TokenDetails;
